const MAX_TENCENT_FILE_SIZE_BYTES = 6 * 1024 * 1024
const MAX_TENCENT_LOCAL_FILE_SIZE_BYTES = 806596 // 806596 bytes is the max file size for the local environment
const isLocalEnv = process.env.RAILS_ENV === 'development'
const maxFileSizeBytes = isLocalEnv ? MAX_TENCENT_LOCAL_FILE_SIZE_BYTES : MAX_TENCENT_FILE_SIZE_BYTES
const MIN_TENCENT_SIDE_PX = 128
const MAX_TENCENT_SIDE_PX = 5000
const TRANSPARENT_FILL_COLOR = '#ffffff'

export function clampDimensions(width, height) {
  let sourceWidth = width
  let sourceHeight = height
  if (sourceWidth < MIN_TENCENT_SIDE_PX || sourceHeight < MIN_TENCENT_SIDE_PX) {
    const scale = MIN_TENCENT_SIDE_PX / Math.min(sourceWidth, sourceHeight)
    sourceWidth = Math.round(sourceWidth * scale)
    sourceHeight = Math.round(sourceHeight * scale)
  }
  if (sourceWidth > MAX_TENCENT_SIDE_PX || sourceHeight > MAX_TENCENT_SIDE_PX) {
    const scale = MAX_TENCENT_SIDE_PX / Math.max(sourceWidth, sourceHeight)
    sourceWidth = Math.round(sourceWidth * scale)
    sourceHeight = Math.round(sourceHeight * scale)
  }
  return { width: sourceWidth, height: sourceHeight }
}

async function renderPngBlob(file, width, height) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  const bitmap = await createImageBitmap(file)
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png', 1)
  })
}

// PNG is lossless and ignores the quality argument, so downscaling dimensions is the only way to shrink it.
// PNG size roughly tracks pixel count, so sqrt(target/actual) aims each step at the size limit.
async function renderPngUnderMaxSize(file, width, height) {
  const blob = await renderPngBlob(file, width, height)
  if (blob.size <= maxFileSizeBytes || Math.min(width, height) <= MIN_TENCENT_SIDE_PX) {
    return blob
  }
  const scale = Math.min(Math.sqrt(maxFileSizeBytes / blob.size) * 0.95, 0.9)
  const next = clampDimensions(Math.round(width * scale), Math.round(height * scale))
  if (next.width * next.height >= width * height) {
    return blob
  }
  return renderPngUnderMaxSize(file, next.width, next.height)
}

async function renderQualityBlobUnderMaxSize(file, width, height, exportMime) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  // JPEG has no alpha channel, so transparent pixels would otherwise be encoded as black.
  if (exportMime === 'image/jpeg') {
    context.fillStyle = TRANSPARENT_FILL_COLOR
    context.fillRect(0, 0, width, height)
  }
  const bitmap = await createImageBitmap(file)
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  function toBlobWithQuality(quality) {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, exportMime, quality)
    })
  }

  async function compressUnderMaxSize(currentQuality) {
    const result = await toBlobWithQuality(currentQuality)
    if (result.size <= maxFileSizeBytes || currentQuality <= 0.1) {
      return result
    }
    const nextQuality = Math.max(0.1, currentQuality - (0.05))
    return compressUnderMaxSize(nextQuality)
  }

  const blob = await compressUnderMaxSize(1)
  if (blob.size > maxFileSizeBytes) {
    return toBlobWithQuality(0.6)
  }
  return blob
}

export async function resizeImageToRequirements(file, { forcePng = false } = {}) {
  const bitmap = await createImageBitmap(file)
  const sourceWidth = bitmap.width
  const sourceHeight = bitmap.height
  bitmap.close()

  const { width: destinationWidth, height: destinationHeight } = clampDimensions(sourceWidth, sourceHeight)
  const mimeType = file.type || 'image/png'

  let blob
  if (forcePng) {
    blob = await renderPngUnderMaxSize(file, destinationWidth, destinationHeight)
  } else {
    const supportsQuality = mimeType === 'image/jpeg' || mimeType === 'image/webp'
    const exportMime = supportsQuality ? mimeType : 'image/jpeg'
    blob = await renderQualityBlobUnderMaxSize(file, destinationWidth, destinationHeight, exportMime)
  }

  const outputMime = blob.type || mimeType
  let extension = 'png'
  if (outputMime === 'image/jpeg') {
    extension = 'jpg'
  } else if (outputMime === 'image/webp') {
    extension = 'webp'
  }
  const baseName = file.name.replace(/\.[^.]+$/, '') || file.name
  return new File([blob], `${baseName}.${extension}`, { type: outputMime })
}
